import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
  Users,
  ArrowRight,
  Merge,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  Search,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DuplicateAccounts({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [mergeDialog, setMergeDialog] = useState({ open: false, match: null });
  const [editingUser, setEditingUser] = useState(null);
  const [editMembershipNum, setEditMembershipNum] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showMissingMembers, setShowMissingMembers] = useState(false);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/duplicate-accounts`, {
        withCredentials: true,
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
      toast.error("Failed to load duplicate accounts");
    } finally {
      setLoading(false);
    }
  };

  const handleTransferMembership = async (fromUserId, toUserId, membershipNumber) => {
    setActionLoading(fromUserId + toUserId);
    try {
      await axios.post(
        `${API}/admin/transfer-membership-number`,
        null,
        {
          params: { from_user_id: fromUserId, to_user_id: toUserId },
          withCredentials: true,
        }
      );
      toast.success(`Membership number ${membershipNumber} transferred successfully!`);
      fetchDuplicates();
    } catch (error) {
      console.error("Error transferring membership:", error);
      toast.error(error.response?.data?.detail || "Failed to transfer membership number");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMergeAccounts = async () => {
    if (!mergeDialog.match) return;
    
    // Determine which account to keep based on user selection
    const keepFirst = mergeDialog.keepFirst !== false; // Default to keeping first (user_without_memnum)
    
    const sourceUser = keepFirst 
      ? mergeDialog.match.user_without_memnum 
      : mergeDialog.match.user_with_memnum;
    const targetUser = keepFirst 
      ? mergeDialog.match.user_with_memnum 
      : mergeDialog.match.user_without_memnum;
    
    setActionLoading("merge");
    try {
      await axios.post(
        `${API}/admin/merge-accounts`,
        null,
        {
          params: { 
            source_user_id: sourceUser.id, 
            target_user_id: targetUser.id 
          },
          withCredentials: true,
        }
      );
      toast.success(`Accounts merged successfully! Kept: ${sourceUser.name}`);
      setMergeDialog({ open: false, match: null });
      fetchDuplicates();
    } catch (error) {
      console.error("Error merging accounts:", error);
      toast.error(error.response?.data?.detail || "Failed to merge accounts");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateMembershipNumber = async (userId) => {
    if (!editMembershipNum.trim()) {
      toast.error("Please enter a membership number");
      return;
    }
    
    setActionLoading(userId);
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { membership_number: editMembershipNum.trim() },
        { withCredentials: true }
      );
      toast.success("Membership number updated!");
      setEditingUser(null);
      setEditMembershipNum("");
      fetchDuplicates();
    } catch (error) {
      console.error("Error updating membership:", error);
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setActionLoading(null);
    }
  };

  // Filter users without membership numbers
  const filteredUsersWithoutMemnum = (data?.users_without_memnum || []).filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="h-6 w-6 text-amber-400" />
                Duplicate Account Detection
              </h1>
              <p className="text-slate-400 text-sm">Find and resolve duplicate user accounts</p>
            </div>
          </div>
          <Button onClick={fetchDuplicates} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-emerald-400">{data?.stats?.total_users_with_memnum || 0}</div>
            <div className="text-sm text-slate-400">With Membership #</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-amber-400">{data?.stats?.total_users_without_memnum || 0}</div>
            <div className="text-sm text-slate-400">Without Membership #</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-red-400">{data?.stats?.duplicate_memnums || 0}</div>
            <div className="text-sm text-slate-400">Duplicate Numbers</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-blue-400">{data?.stats?.potential_name_matches || 0}</div>
            <div className="text-sm text-slate-400">Potential Matches</div>
          </Card>
        </div>

        {/* Explanation */}
        <Card className="bg-amber-500/10 border-amber-500/30 p-4 mb-6">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              <p className="font-medium mb-1">Why do duplicates occur?</p>
              <p className="text-amber-300/80">
                When members are bulk-uploaded AND also self-register with a different email or name format 
                (e.g., "Hock Seng Tan" vs "Tan Hock Seng"), two accounts are created. The bulk-uploaded 
                account has the membership number, but the self-registered account (used for game signups) does not.
              </p>
            </div>
          </div>
        </Card>

        {/* Potential Name Matches */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <div className="p-4 border-b border-slate-700">
            <h2 className="font-semibold text-white">Potential Duplicate Accounts</h2>
            <p className="text-sm text-slate-400">Accounts with similar names - one has membership number, one doesn't</p>
          </div>
          
          {data?.potential_name_matches?.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-emerald-500" />
              <p>No potential duplicates found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {data?.potential_name_matches?.map((match, idx) => (
                <div key={idx} className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* User WITHOUT membership number */}
                    <div className="flex-1 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <XCircle className="h-4 w-4 text-red-400" />
                        <span className="text-xs text-red-400 font-medium">NO MEMBERSHIP #</span>
                      </div>
                      <p className="font-medium text-white">{match.user_without_memnum.name}</p>
                      <p className="text-sm text-slate-400">{match.user_without_memnum.email}</p>
                      {match.user_without_memnum.is_authorized && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-xs mt-1">Active</Badge>
                      )}
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center justify-center">
                      <ArrowRight className="h-6 w-6 text-slate-400 hidden md:block" />
                      <div className="h-6 w-px bg-slate-600 md:hidden" />
                    </div>

                    {/* User WITH membership number */}
                    <div className="flex-1 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                        <span className="text-xs text-emerald-400 font-medium">HAS MEMBERSHIP #</span>
                      </div>
                      <p className="font-medium text-white">{match.user_with_memnum.name}</p>
                      <p className="text-sm text-slate-400">{match.user_with_memnum.email}</p>
                      <Badge className="bg-blue-500/20 text-blue-400 text-xs mt-1 font-mono">
                        #{match.user_with_memnum.membership_number}
                      </Badge>
                    </div>
                  </div>

                  {/* Matching parts */}
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                    <span>Matching name parts:</span>
                    {match.matching_name_parts.map((part, i) => (
                      <Badge key={i} className="bg-slate-700 text-slate-300">{part}</Badge>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleTransferMembership(
                        match.user_with_memnum.id,
                        match.user_without_memnum.id,
                        match.user_with_memnum.membership_number
                      )}
                      disabled={actionLoading === match.user_with_memnum.id + match.user_without_memnum.id}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {actionLoading === match.user_with_memnum.id + match.user_without_memnum.id ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ArrowRight className="h-4 w-4 mr-2" />
                      )}
                      Transfer Membership # Only
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setMergeDialog({ open: true, match })}
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                    >
                      <Merge className="h-4 w-4 mr-2" />
                      Merge Accounts
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Duplicate Membership Numbers */}
        {Object.keys(data?.duplicates_by_membership_number || {}).length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <div className="p-4 border-b border-slate-700">
              <h2 className="font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                Duplicate Membership Numbers
              </h2>
              <p className="text-sm text-slate-400">Multiple users with the same membership number</p>
            </div>
            <div className="divide-y divide-slate-700">
              {Object.entries(data?.duplicates_by_membership_number || {}).map(([memnum, users]) => (
                <div key={memnum} className="p-4">
                  <Badge className="bg-red-500/20 text-red-400 font-mono mb-2">#{memnum}</Badge>
                  <div className="space-y-2">
                    {users.map((user, idx) => (
                      <div key={idx} className="bg-slate-900/50 p-2 rounded text-sm">
                        <p className="font-medium text-white">{user.name}</p>
                        <p className="text-slate-400">{user.email}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Users Missing Membership Numbers - Collapsible Section */}
        <Card className="bg-slate-800/50 border-slate-700 mb-6">
          <div 
            className="p-4 border-b border-slate-700 cursor-pointer hover:bg-slate-700/30 transition-colors"
            onClick={() => setShowMissingMembers(!showMissingMembers)}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-white flex items-center gap-2">
                  <Edit className="h-5 w-5 text-amber-400" />
                  Users Without Membership Number ({data?.stats?.total_users_without_memnum || 0})
                </h2>
                <p className="text-sm text-slate-400">Click to {showMissingMembers ? 'hide' : 'show'} and edit membership numbers</p>
              </div>
              <Badge className="bg-amber-500/20 text-amber-400">
                {showMissingMembers ? 'Hide' : 'Show All'}
              </Badge>
            </div>
          </div>
          
          {showMissingMembers && (
            <div className="p-4">
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-900/50 border-slate-600"
                />
              </div>
              
              {filteredUsersWithoutMemnum.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  {searchTerm ? 'No users match your search' : 'All users have membership numbers!'}
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredUsersWithoutMemnum.map((u) => (
                    <div key={u.id} className="bg-slate-900/50 p-3 rounded-lg flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-white">{u.name}</p>
                        <p className="text-sm text-slate-400">{u.email}</p>
                        {u.last_login && (
                          <p className="text-xs text-slate-500">
                            Last login: {new Date(u.last_login).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      
                      {editingUser === u.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="SCC001"
                            value={editMembershipNum}
                            onChange={(e) => setEditMembershipNum(e.target.value)}
                            className="w-32 h-8 bg-slate-800 border-slate-600 text-sm"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateMembershipNumber(u.id)}
                            disabled={actionLoading === u.id}
                            className="bg-emerald-600 hover:bg-emerald-700 h-8"
                          >
                            {actionLoading === u.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setEditingUser(null); setEditMembershipNum(""); }}
                            className="h-8"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingUser(u.id); setEditMembershipNum(""); }}
                          className="border-slate-600 text-slate-300"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Add #
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Merge Dialog - Enhanced with full details */}
        <Dialog open={mergeDialog.open} onOpenChange={(open) => setMergeDialog({ open, match: open ? mergeDialog.match : null })}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Merge className="h-5 w-5 text-amber-400" />
                Merge Accounts - Compare Details
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Review both accounts and choose which one to keep. All data from the deleted account will be transferred.
              </DialogDescription>
            </DialogHeader>
            
            {mergeDialog.match && (
              <div className="py-4 space-y-4">
                {/* Side by side comparison */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Account 1 - User without memnum (usually the active one) */}
                  <div 
                    className={`rounded-lg p-4 cursor-pointer transition-all ${
                      mergeDialog.keepFirst !== false 
                        ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                        : 'bg-slate-800/50 border border-slate-600 hover:border-slate-500'
                    }`}
                    onClick={() => setMergeDialog({...mergeDialog, keepFirst: true})}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-emerald-400">ACCOUNT A</span>
                      {mergeDialog.keepFirst !== false && (
                        <Badge className="bg-emerald-500 text-white text-xs">KEEP THIS</Badge>
                      )}
                    </div>
                    <p className="font-semibold text-white text-lg">{mergeDialog.match.user_without_memnum.name}</p>
                    <p className="text-sm text-slate-400 mb-3">{mergeDialog.match.user_without_memnum.email}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Membership #:</span>
                        <span className="text-red-400">{mergeDialog.match.user_without_memnum.membership_number || 'None'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Phone:</span>
                        <span className="text-white">{mergeDialog.match.user_without_memnum.phone || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Status:</span>
                        <span className={mergeDialog.match.user_without_memnum.is_authorized ? 'text-emerald-400' : 'text-red-400'}>
                          {mergeDialog.match.user_without_memnum.is_authorized ? 'Active' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Teams:</span>
                        <span className="text-white">{mergeDialog.match.user_without_memnum.teams?.length || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Created:</span>
                        <span className="text-slate-300 text-xs">
                          {mergeDialog.match.user_without_memnum.created_at 
                            ? new Date(mergeDialog.match.user_without_memnum.created_at).toLocaleDateString()
                            : 'Unknown'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Last Login:</span>
                        <span className="text-slate-300 text-xs">
                          {mergeDialog.match.user_without_memnum.last_login 
                            ? new Date(mergeDialog.match.user_without_memnum.last_login).toLocaleDateString()
                            : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Account 2 - User with memnum (usually bulk uploaded) */}
                  <div 
                    className={`rounded-lg p-4 cursor-pointer transition-all ${
                      mergeDialog.keepFirst === false 
                        ? 'bg-emerald-500/20 border-2 border-emerald-500' 
                        : 'bg-slate-800/50 border border-slate-600 hover:border-slate-500'
                    }`}
                    onClick={() => setMergeDialog({...mergeDialog, keepFirst: false})}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-blue-400">ACCOUNT B</span>
                      {mergeDialog.keepFirst === false && (
                        <Badge className="bg-emerald-500 text-white text-xs">KEEP THIS</Badge>
                      )}
                    </div>
                    <p className="font-semibold text-white text-lg">{mergeDialog.match.user_with_memnum.name}</p>
                    <p className="text-sm text-slate-400 mb-3">{mergeDialog.match.user_with_memnum.email}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Membership #:</span>
                        <span className="text-emerald-400 font-mono">#{mergeDialog.match.user_with_memnum.membership_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Phone:</span>
                        <span className="text-white">{mergeDialog.match.user_with_memnum.phone || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Status:</span>
                        <span className={mergeDialog.match.user_with_memnum.is_authorized ? 'text-emerald-400' : 'text-red-400'}>
                          {mergeDialog.match.user_with_memnum.is_authorized ? 'Active' : 'Pending'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Teams:</span>
                        <span className="text-white">{mergeDialog.match.user_with_memnum.teams?.length || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Created:</span>
                        <span className="text-slate-300 text-xs">
                          {mergeDialog.match.user_with_memnum.created_at 
                            ? new Date(mergeDialog.match.user_with_memnum.created_at).toLocaleDateString()
                            : 'Unknown'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Last Login:</span>
                        <span className="text-slate-300 text-xs">
                          {mergeDialog.match.user_with_memnum.last_login 
                            ? new Date(mergeDialog.match.user_with_memnum.last_login).toLocaleDateString()
                            : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm">
                  <p className="font-medium text-blue-400 mb-1">💡 Recommendation:</p>
                  <p className="text-blue-200/80">
                    {mergeDialog.match.user_without_memnum.last_login && !mergeDialog.match.user_with_memnum.last_login
                      ? `Keep Account A (${mergeDialog.match.user_without_memnum.name}) - this is the actively used account with login history.`
                      : mergeDialog.match.user_with_memnum.last_login && !mergeDialog.match.user_without_memnum.last_login
                      ? `Keep Account B (${mergeDialog.match.user_with_memnum.name}) - this is the actively used account with login history.`
                      : 'Keep the account that the player actively uses to log in. The membership number will be transferred automatically.'}
                  </p>
                </div>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-400 mb-1">What will happen:</p>
                  <ul className="text-amber-200/80 list-disc list-inside space-y-1">
                    <li>Membership number will be transferred to kept account</li>
                    <li>Team memberships will be combined</li>
                    <li>Game signups will be transferred</li>
                    <li>The other account will be permanently deleted</li>
                  </ul>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setMergeDialog({ open: false, match: null })}>
                Cancel
              </Button>
              <Button
                onClick={handleMergeAccounts}
                disabled={actionLoading === "merge"}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {actionLoading === "merge" ? (
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Merge className="h-4 w-4 mr-2" />
                )}
                Merge (Keep {mergeDialog.keepFirst !== false ? 'Account A' : 'Account B'})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
